/**
 * Best-execution reporting — phase 8 of broker integration.
 *
 * Computes SEC-style execution quality metrics per broker and venue. Required
 * by SEC Rule 606/607 (US) and analogous by NGX X-Compliance rules: every
 * broker handling retail order flow must publish a quarterly execution
 * report. NigaStock aggregates across all the user's linked brokers.
 *
 * Metrics produced:
 *   - fillRate           — % of orders that filled vs. were cancelled/rejected
 *   - avgSlippageBps     — fill price vs. decision price at placement (+ = paid up)
 *   - priceImprovementPct — % of orders that filled better than the limit
 *   - avgLatencyMs       — order-placement to first-fill latency
 *   - totalFeesKobo      — all-in commission + exchange fees
 *   - notionalKobo       — total traded notional
 *
 * All math runs on the same audit record stream the reconciliation agent uses
 * so numbers tie back to the S3 Object-Lock archive. The report itself is
 * emitted as a JSONL record each month, stored at
 *   s3://nigastock-audit/best-execution/{userSub}/{YYYY-MM}/report.jsonl
 * and kept for 7 years (SEC Rule 30.13).
 */

import type { BrokerId } from './brokers';

export interface FillRecord {
  clOrdId: string;
  brokerId: BrokerId;
  symbol: string;
  securityType: 'EQUITY' | 'BOND' | 'ETF';
  side: 'BUY' | 'SELL';
  decisionKobo: string;       // price at placement (mark at time of decision)
  limitKobo?: string;         // user's limit (if any)
  fillKobo: string;           // actual fill avg price
  quantity: number;
  placedAt: string;            // ISO
  firstFillAt?: string;        // ISO
  status: 'FILLED' | 'PARTIAL' | 'CANCELLED' | 'REJECTED';
  feeKobo: string;             // all-in fees for this fill
}

export interface BrokerMetrics {
  brokerId: BrokerId;
  orderCount: number;
  filledCount: number;
  fillRate: number;             // 0..1
  avgSlippageBps: number;       // signed; + = worse for user
  priceImprovementPct: number;  // 0..1
  avgLatencyMs: number;
  totalFeesKobo: string;
  notionalKobo: string;
}

/**
 * Compute per-broker metrics from a fill/order stream. Orders without a
 * first-fill timestamp are excluded from latency math but still count for
 * fillRate. Slippage is measured as:
 *   BUY:  (fill - decision) / decision * 10_000
 *   SELL: (decision - fill) / decision * 10_000
 * So positive slippage always means "worse for the user".
 */
export function computeMetrics(records: FillRecord[]): BrokerMetrics[] {
  const by = new Map<BrokerId, FillRecord[]>();
  for (const r of records) {
    if (!by.has(r.brokerId)) by.set(r.brokerId, []);
    by.get(r.brokerId)!.push(r);
  }
  const out: BrokerMetrics[] = [];
  by.forEach((rs, brokerId) => {
    const filled = rs.filter((r) => r.status === 'FILLED' || r.status === 'PARTIAL');
    const slippages: number[] = [];
    const latencies: number[] = [];
    let priceImprovementCount = 0;
    let totalFees = 0n, notional = 0n;

    for (const r of filled) {
      const dec = Number(r.decisionKobo), fil = Number(r.fillKobo);
      const slip = r.side === 'BUY' ? (fil - dec) / dec * 10_000 : (dec - fil) / dec * 10_000;
      slippages.push(slip);
      if (r.firstFillAt) latencies.push(new Date(r.firstFillAt).getTime() - new Date(r.placedAt).getTime());
      if (r.limitKobo) {
        const lim = Number(r.limitKobo);
        // Price improvement = filled below limit on BUY, above limit on SELL
        if (r.side === 'BUY' && fil < lim) priceImprovementCount++;
        if (r.side === 'SELL' && fil > lim) priceImprovementCount++;
      }
      totalFees += BigInt(r.feeKobo);
      notional += BigInt(r.fillKobo) * BigInt(r.quantity);
    }
    const avgSlip = slippages.length > 0 ? slippages.reduce((a, b) => a + b, 0) / slippages.length : 0;
    const avgLat = latencies.length > 0 ? latencies.reduce((a, b) => a + b, 0) / latencies.length : 0;
    const orderedWithLimit = filled.filter((r) => r.limitKobo).length || 1;
    out.push({
      brokerId,
      orderCount: rs.length,
      filledCount: filled.length,
      fillRate: rs.length > 0 ? filled.length / rs.length : 0,
      avgSlippageBps: avgSlip,
      priceImprovementPct: priceImprovementCount / orderedWithLimit,
      avgLatencyMs: Math.round(avgLat),
      totalFeesKobo: totalFees.toString(),
      notionalKobo: notional.toString(),
    });
  });
  return out.sort((a, b) => a.brokerId.localeCompare(b.brokerId));
}

/** Roll-up across all brokers for the headline summary card. */
export function summarize(records: FillRecord[]) {
  const m = computeMetrics(records);
  const total = m.reduce((a, b) => a + b.orderCount, 0);
  const filled = m.reduce((a, b) => a + b.filledCount, 0);
  const notional = m.reduce((a, b) => a + BigInt(b.notionalKobo), 0n);
  const fees = m.reduce((a, b) => a + BigInt(b.totalFeesKobo), 0n);
  // Weighted-average slippage by filled count
  const slipNum = m.reduce((a, b) => a + b.avgSlippageBps * b.filledCount, 0);
  const piNum = m.reduce((a, b) => a + b.priceImprovementPct * b.filledCount, 0);
  return {
    orderCount: total,
    filledCount: filled,
    fillRate: total > 0 ? filled / total : 0,
    avgSlippageBps: filled > 0 ? slipNum / filled : 0,
    priceImprovementPct: filled > 0 ? piNum / filled : 0,
    totalFeesKobo: fees.toString(),
    notionalKobo: notional.toString(),
  };
}

export function seedDemoFills(): FillRecord[] {
  const iso = (hoursAgo: number) => new Date(Date.now() - hoursAgo * 3600_000).toISOString();
  return [
    { clOrdId: 'c1', brokerId: 'bamboo', symbol: 'ZENITHBANK', securityType: 'EQUITY', side: 'BUY',
      decisionKobo: '442500', limitKobo: '443000', fillKobo: '442450', quantity: 1000,
      placedAt: iso(6), firstFillAt: iso(5.99), status: 'FILLED', feeKobo: '6420' },
    { clOrdId: 'c2', brokerId: 'bamboo', symbol: 'DANGCEM', securityType: 'EQUITY', side: 'BUY',
      decisionKobo: '6825000', limitKobo: '6830000', fillKobo: '6830000', quantity: 50,
      placedAt: iso(9), firstFillAt: iso(8.996), status: 'FILLED', feeKobo: '4820' },
    { clOrdId: 'c3', brokerId: 'chd', symbol: 'FGN29-13.53', securityType: 'BOND', side: 'BUY',
      decisionKobo: '94200000', limitKobo: '94250000', fillKobo: '94180000', quantity: 5000,
      placedAt: iso(22), firstFillAt: iso(21.95), status: 'FILLED', feeKobo: '945000' },
    { clOrdId: 'c4', brokerId: 'meristem', symbol: 'NEWGOLD', securityType: 'ETF', side: 'BUY',
      decisionKobo: '5820000', limitKobo: '5825000', fillKobo: '5822000', quantity: 2,
      placedAt: iso(36), firstFillAt: iso(35.98), status: 'PARTIAL', feeKobo: '1890' },
    { clOrdId: 'c5', brokerId: 'bamboo', symbol: 'MTNN', securityType: 'EQUITY', side: 'SELL',
      decisionKobo: '2380000', limitKobo: '2375000', fillKobo: '2385000', quantity: 200,
      placedAt: iso(48), firstFillAt: iso(47.7), status: 'FILLED', feeKobo: '28400' },
    { clOrdId: 'c6', brokerId: 'cordros', symbol: 'GTCO', securityType: 'EQUITY', side: 'BUY',
      decisionKobo: '7510000', limitKobo: '7515000', fillKobo: '7515000', quantity: 500,
      placedAt: iso(60), firstFillAt: undefined, status: 'CANCELLED', feeKobo: '0' },
    { clOrdId: 'c7', brokerId: 'chd', symbol: 'FGN32-18.50', securityType: 'BOND', side: 'SELL',
      decisionKobo: '101400000', limitKobo: '101300000', fillKobo: '101420000', quantity: 3000,
      placedAt: iso(72), firstFillAt: iso(71.9), status: 'FILLED', feeKobo: '605000' },
    { clOrdId: 'c8', brokerId: 'meristem', symbol: 'VETGRIF30', securityType: 'ETF', side: 'BUY',
      decisionKobo: '385500', limitKobo: '385500', fillKobo: '385500', quantity: 1000,
      placedAt: iso(84), firstFillAt: iso(83.99), status: 'FILLED', feeKobo: '5780' },
  ];
}
